import { post } from "@rails/request.js"

// Shared PWA platform detection + install-prompt capture.
//
// The beforeinstallprompt listener lives here (module scope, registered at
// import time) rather than in a controller so it can capture the event even
// if it fires before a Turbo-Stream-appended banner/modal exists in the DOM.
// Consumers (Stimulus controllers) read the captured prompt via
// hasInstallPrompt()/takeInstallPrompt() and can listen for the
// "pwa:installable" document event to react to a late-arriving prompt.

// Check if the app is already running in standalone/PWA mode
export function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches ||
         window.navigator.standalone === true
}

// Detect any iOS/iPadOS browser (Safari or otherwise).
// Catches iOS Chrome/Firefox/Edge which don't fire beforeinstallprompt, as
// well as iPads that report "Macintosh" in their UA but have touch support.
export function isIOS() {
  const ua = navigator.userAgent
  return /iPad|iPhone|iPod/.test(ua) ||
         (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
}

// Detect Safari on macOS (supports "Add to Dock" since Sonoma).
// Safe to call only after isIOS() returns false (which handles iPads that
// report "Macintosh" in their UA).
export function isSafariMac() {
  const ua = navigator.userAgent
  const isMac = /Macintosh/.test(ua)
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|Chromium/.test(ua)
  return isMac && isSafari
}

// Check if the browser can receive web push notifications.
// iOS Safari only supports Web Push in standalone (PWA) mode.
// navigator.standalone is an iOS Safari-specific property:
//   undefined → not iOS Safari
//   false    → iOS Safari in regular browser (push won't work)
//   true     → iOS Safari in standalone PWA (push works)
export function supportsPush() {
  return ("Notification" in window) &&
         ("serviceWorker" in navigator) &&
         window.navigator.standalone !== false
}

// Current Notification permission, or "unsupported" if the browser has no
// Notification API at all.
export function notificationPermission() {
  return "Notification" in window ? Notification.permission : "unsupported"
}

let deferredPrompt = null

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault()
    deferredPrompt = event
    document.dispatchEvent(new CustomEvent("pwa:installable"))
  })
}

export const hasInstallPrompt = () => deferredPrompt !== null

export const takeInstallPrompt = () => {
  const prompt = deferredPrompt
  deferredPrompt = null
  return prompt
}

// Log the install activity to the server (best-effort — never blocks the
// install flow, and shared here so the guide modal and the legacy banner
// log identically).
export async function logPwaInstall(method) {
  try {
    await post("/user_activities/activity", {
      body: JSON.stringify({
        event: "pwa.install",
        referrer: method
      })
    })
  } catch {
    // Best-effort — don't block the install flow
  }
}
